import { createFileRoute, Link } from "@tanstack/react-router";
import { FormProvider, useForm } from "react-hook-form";
import { InferType, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Modal,
    ModalBody,
    ModalContent,
    useDisclosure,
    CardFooter,
} from "@heroui/react";
import TextInput from "@/components/inputs/TextInput";
import sentinelLogo from "@/assets/images/Sentinel_Horizontal_TM.png";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import sentinelHelmet from "@/assets/images/sentinel-helmet.png";

export const Route = createFileRoute("/(auth)/auth")({
    component: RouteComponent,
});

const schema = object().shape({
    username: string().email().required().label("Email"),
    password: string().min(8).required().label("Password"),
});

const firstTimeForm = object().shape({
    learnMoreEmail: string().email().required().label("Email"),
});

type FormType = InferType<typeof schema>;

function RouteComponent() {
    const { axiosRequest } = useRequest();
    const { isOpen, onOpen, onOpenChange } = useDisclosure();
    const { signIn, signInMutate } = useAuth();
    const f = useForm<FormType>({
        resolver: yupResolver(schema),
    });

    const handleSubmit = (data: FormType) => {
        signIn(data);
    };

    const firstTimeF = useForm({
        resolver: yupResolver(firstTimeForm),
        mode: "onChange",
    });

    const handleFirst = async ({
        learnMoreEmail: email,
    }: {
        learnMoreEmail: string;
    }) => {
        axiosRequest({
            url: "learn-more/",
            method: "post",
            data: {
                email,
            },
        }).then(() => {
            onOpen();
        });
    };

    const blurb = (
        <div className="p-4 flex flex-col gap-4 pt-12 w-full">
            <div className=" font-bold text-lg">Sentinel® Insights</div>
            <div className="text-3xl font-semibold">
                Profitable and confident Nitrogen management
            </div>
            <div>
                Sentinel optimizes your nitrogen applications to achieve the
                most profitable outcome on your farm. It does this by measuring
                crop N demand and suggesting when, where, and how much N
                fertilizer to apply. Sentinel has been used to achieve NCGA Low
                N contest championship wins and has delivered $24/ac improvement
                in profitability in benchmarking trials. Are you ready to use
                Sentinel to revolutionize your Nitrogen management?
            </div>
        </div>
    );

    const [isVisible, setIsVisible] = useState(false);

    return (
        <div className="flex flex-col items-center md:grid md:grid-cols-[1fr_2fr] min-h-screen w-full md:h-screen md:justify-items-center md:p-0">
            <div className="w-full h-full hidden md:block overflow-hidden  ">
                <div className="h-full w-full flex flex-col overflow-hidden text-primary-500">
                    {blurb}
                    <div className="w-full h-full flex justify-center items-center">
                        <img
                            src={sentinelHelmet}
                            className="w-[20rem] h-auto object-contain"
                        />
                    </div>
                </div>
            </div>
            <div className="pt-8 px-2 w-full md:overflow-y-auto md:h-screen flex flex-col items-center justify-center">
                <Card className="w-full max-w-[30rem] mx-auto">
                    <CardHeader></CardHeader>
                    <CardBody>
                        <FormProvider {...f}>
                            <form onSubmit={f.handleSubmit(handleSubmit)}>
                                <div className="flex flex-col  w-[90%] mx-auto  items-stretch  md:max-w-[25rem] gap-2">
                                    <img
                                        src={sentinelLogo}
                                        className="px-8 mb-4"
                                    />
                                    <TextInput name="username" label="Email" />
                                    <TextInput
                                        name="password"
                                        label="Password"
                                        type={isVisible ? "text" : "password"}
                                        endContent={
                                            <Button
                                                isIconOnly
                                                variant="flat"
                                                size="sm"
                                                onPress={() =>
                                                    setIsVisible(!isVisible)
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        isVisible
                                                            ? faEyeSlash
                                                            : faEye
                                                    }
                                                />
                                            </Button>
                                        }
                                    ></TextInput>
                                    {signInMutate.isError && (
                                        <div className="text-sm text-red-500">
                                            Username or password is incorrect
                                        </div>
                                    )}
                                    <Link
                                        to="/password/reset"
                                        className="text-brandGreen-500 text-sm my-2"
                                    >
                                        Forgot Password?
                                    </Link>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        variant="solid"
                                        isLoading={signInMutate.isPending}
                                    >
                                        Sign In
                                    </Button>
                                </div>
                            </form>
                        </FormProvider>
                        <div className="mt-8">
                            <FormProvider {...firstTimeF}>
                                <form
                                    onSubmit={firstTimeF.handleSubmit(
                                        handleFirst,
                                    )}
                                >
                                    <div className="flex flex-col w-[90%] mx-auto md:max-w-[25rem] gap-4">
                                        First time here?
                                        <TextInput
                                            name="learnMoreEmail"
                                            label="Email"
                                        />
                                        <Button
                                            type="submit"
                                            color="primary"
                                            variant="solid"
                                            fullWidth
                                        >
                                            Learn More
                                        </Button>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </CardBody>
                    <CardFooter></CardFooter>
                </Card>
            </div>
            <div className="md:hidden mt-12 text-primary-500 flex-1 flex flex-col ">
                {blurb}
                <div className="w-full flex justify-center pb-12">
                    <img
                        src={sentinelHelmet}
                        className="w-[60%] h-auto object-contain "
                    />
                </div>
            </div>
            {isOpen && (
                <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
                    <ModalContent>
                        <ModalBody>
                            Thank you for your interest in Sentinel! Please
                            check your email for a message about next steps to
                            get started.
                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}
        </div>
    );
}
